import { useState } from 'react';
import { Customer, CustomerAccountMovement } from '../../types';
import { db } from '../../db';
import { normalizePhone } from '../../src/utils/phoneUtils';

interface SyncQueueItem {
  type: string;
  action: string;
  data: unknown;
  status: string;
  timestamp: number;
}

export function useCustomers(
  addToSyncQueue: (item: SyncQueueItem) => Promise<unknown>,
  assertMutationAllowed: (fnName: string) => void
) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerAccountMovements, setCustomerAccountMovements] = useState<CustomerAccountMovement[]>([]);

  const addCustomerAccountMovement = async (item: CustomerAccountMovement) => {
    assertMutationAllowed('addCustomerAccountMovement');
    await db.customerAccountMovements.put(item);

    await db.syncStack.add({
      type: 'customerAccountMovement',
      action: 'create',
      data: item,
      timestamp: Date.now(),
      status: 'pending'
    });

    const existingCustomer = await db.customers.get(item.customerId);

    if (existingCustomer) {
      let nextDebt = existingCustomer.debt || 0;
      let nextTotalSpent = existingCustomer.totalSpent || 0;

      if (item.status !== 'cancelled') {
        if (item.type === 'debt_add') {
          nextDebt += item.amount;
          nextTotalSpent += item.amount;
        } else if (item.type === 'payment') {
          nextDebt -= item.amount;
        } else if (item.type === 'adjustment') {
          nextDebt = item.amount;
        }
      }

      const updatedCustomer = {
        ...existingCustomer,
        debt: nextDebt,
        totalSpent: nextTotalSpent,
        lastVisit: item.date
      };

      await db.customers.put(updatedCustomer);

      setCustomers(prev =>
        prev.map(customer =>
          customer.id === item.customerId ? updatedCustomer : customer
        )
      );

      await addToSyncQueue({
        type: 'customer',
        action: 'update',
        data: updatedCustomer,
        timestamp: Date.now(),
        status: 'pending'
      });
    }

    setCustomerAccountMovements(prev => [item, ...prev]);
  };

  const addCustomer = async (item: Customer) => {
    assertMutationAllowed('addCustomer');
    const normalizedPhone = normalizePhone(item.phone || '');
    const normalizedItem = { ...item, phone: normalizedPhone };
    if (normalizedPhone) {
      const duplicate = customers.find(c => normalizePhone(c.phone || '') === normalizedPhone);
      if (duplicate) throw new Error('DUPLICATE_PHONE');
    }
    await db.customers.add(normalizedItem);
    await db.syncStack.add({ type: 'customer', action: 'create', data: normalizedItem, status: 'pending', timestamp: Date.now() });
    setCustomers(prev => [...prev, normalizedItem]);
  };

  const updateCustomer = async (item: Customer) => {
    assertMutationAllowed('updateCustomer');
    await db.customers.put(item);
    await addToSyncQueue({ type: 'customer', action: 'update', data: item, status: 'pending', timestamp: Date.now() });
    setCustomers(prev => prev.map(c => c.id === item.id ? item : c));
  };

  const deleteCustomer = async (id: string) => {
    await db.customers.delete(id);
    await db.syncStack.add({ type: 'customer', action: 'delete', data: { id }, status: 'pending', timestamp: Date.now() });
    setCustomers(prev => prev.filter(c => c.id !== id));
  };

  const mergeLoyaltyCustomer = async (crmCustomerId: string, loyaltyCustomerId: string) => {
    const crmCustomer = customers.find(c => c.id === crmCustomerId);
    const loyaltyCustomer = customers.find(c => c.id === loyaltyCustomerId);
    if (!crmCustomer || !loyaltyCustomer) return;

    const merged: Customer = {
      ...crmCustomer,
      stamps: (crmCustomer.stamps || 0) + (loyaltyCustomer.stamps || 0),
      loyaltyPoints: (crmCustomer.loyaltyPoints || 0) + (loyaltyCustomer.loyaltyPoints || 0),
      loyaltyRedeemed: (crmCustomer.loyaltyRedeemed || 0) + (loyaltyCustomer.loyaltyRedeemed || 0),
      loyaltyOnly: false,
    };

    await db.customers.put(merged);
    await db.customers.delete(loyaltyCustomerId);
    await addToSyncQueue({ type: 'customer', action: 'update', data: merged, status: 'pending', timestamp: Date.now() });
    await addToSyncQueue({ type: 'customer', action: 'delete', data: { id: loyaltyCustomerId }, status: 'pending', timestamp: Date.now() });
    setCustomers(prev => prev.filter(c => c.id !== loyaltyCustomerId).map(c => c.id === crmCustomerId ? merged : c));
  };

  return {
    customers,
    setCustomers,
    customerAccountMovements,
    setCustomerAccountMovements,
    addCustomerAccountMovement,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    mergeLoyaltyCustomer,
  };
}
